import { useRef, useEffect, useState } from 'react';
import DiceBox from '@3d-dice/dice-box-threejs';

/**
 * Standalone dice roller test page
 * Tests the dice-box-threejs library in isolation
 */
export function DiceTest() {
  const containerRef = useRef<HTMLDivElement>(null);
  const diceBoxRef = useRef<DiceBox | null>(null);
  const [status, setStatus] = useState('Initializing...');
  const [result, setResult] = useState<string>('');
  const [notation, setNotation] = useState('1d20');

  useEffect(() => {
    if (!containerRef.current) return;

    const containerId = 'dice-test-container';
    containerRef.current.id = containerId;

    const init = async () => {
      try {
        setStatus('Creating DiceBox...');
        console.log('[DiceTest] Creating DiceBox...');

        const diceBox = new DiceBox(`#${containerId}`, {
          assetPath: '/dice/',
          framerate: 1 / 60,
          sounds: false,
          volume: 0,
          color_spotlight: 0xd4af37, // Gold
          shadows: true,
          theme_surface: 'green-felt',
          theme_colorset: 'bronze',
          theme_texture: 'bronze03',
          theme_material: 'metal',
          gravity_multiplier: 400,
          light_intensity: 1.0,
          baseScale: 100,
          strength: 1,
        });

        setStatus('Initializing DiceBox...');
        console.log('[DiceTest] Calling initialize()...');

        await diceBox.initialize();

        console.log('[DiceTest] Initialized!', diceBox.initialized);
        setStatus(`Ready! (initialized: ${diceBox.initialized})`);

        diceBoxRef.current = diceBox;
      } catch (err) {
        console.error('[DiceTest] Init failed:', err);
        setStatus(`Error: ${err}`);
      }
    };

    init();
  }, []);

  const handleRoll = async () => {
    if (!diceBoxRef.current) {
      setResult('DiceBox not ready');
      return;
    }

    setResult('Rolling...');
    console.log('[DiceTest] Rolling:', notation);

    try {
      const results = await diceBoxRef.current.roll(notation);
      console.log('[DiceTest] Results:', results);
      setResult(JSON.stringify(results, null, 2));
    } catch (err) {
      console.error('[DiceTest] Roll error:', err);
      setResult(`Error: ${err}`);
    }
  };

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#1a1a2e',
      color: '#fff',
      fontFamily: 'system-ui, sans-serif',
    }}>
      {/* Controls */}
      <div style={{
        padding: '20px',
        backgroundColor: '#16213e',
        borderBottom: '2px solid #d4af37',
        display: 'flex',
        gap: '20px',
        alignItems: 'center',
      }}>
        <h1 style={{ margin: 0, color: '#d4af37' }}>Dice Roller Test</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={notation}
            onChange={(e) => setNotation(e.target.value)}
            placeholder="e.g. 2d6+3"
            style={{
              padding: '10px 15px',
              fontSize: '16px',
              backgroundColor: '#1a1a2e',
              color: '#fff',
              border: '1px solid #d4af37',
              borderRadius: '4px',
              width: '150px',
            }}
          />
          <button
            onClick={handleRoll}
            style={{
              padding: '10px 30px',
              fontSize: '16px',
              backgroundColor: '#d4af37',
              color: '#1a1a2e',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Roll!
          </button>
        </div>
        <div style={{ color: '#888' }}>Status: {status}</div>
      </div>

      {/* Dice container */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          position: 'relative',
          backgroundColor: '#0f3460',
        }}
      />

      {/* Results */}
      {result && (
        <div style={{
          padding: '15px 20px',
          backgroundColor: '#16213e',
          borderTop: '2px solid #d4af37',
          maxHeight: '200px',
          overflow: 'auto',
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#d4af37' }}>Result:</h3>
          <pre style={{
            margin: 0,
            fontSize: '14px',
            color: '#aaa',
            whiteSpace: 'pre-wrap',
          }}>
            {result}
          </pre>
        </div>
      )}
    </div>
  );
}
